<template>
	<div class="equivalences-container">
		<!-- Enhanced Main Layout with Glass Effect -->
		<div class="glass-container backdrop-blur-lg bg-white/80 rounded-xl shadow-xl border border-white/20 p-4">
			<!-- Header Action Buttons (shown when footer buttons are not visible) -->
			<div v-show="showHeaderButtons" class="header-actions-wrapper mb-4">
				<div class="flex flex-col sm:flex-row gap-3 justify-end">
					<vs-button
						class="cancel-btn"
						type="border"
						color="danger"
						icon-pack="feather"
						icon="icon-x"
						size="small"
						@click.prevent="handleCancel"
					>
						{{ $t("Cancel") }}
					</vs-button>

					<vs-button
						class="save-btn"
						color="primary"
						icon-pack="feather"
						icon="icon-save"
						size="small"
						:loading="isProcessing"
						@click="validateUploadEquivalents"
					>
						{{ $t("Save") }}
					</vs-button>
				</div>
			</div>
			<div class="grid grid-cols-1 xl:grid-cols-2 gap-8">
				<!-- Column Mapping Section -->
				<div class="mapping-section">
					<div class="section-header">
						<h3 class="text-xl font-semibold text-gray-800 flex items-center gap-3">
							<div class="icon-wrapper p-2 bg-primary/10 rounded-lg">
								<feather-icon icon="ColumnsIcon" class="w-5 h-5 text-primary" />
							</div>
							{{ $t("Routing.Title.MatchColumn") }}
							<span :title="$t('Routing.Messages.MatchColumnSelectionRoutingP2Tooltip')">
								<feather-icon icon="HelpCircleIcon" class="w-4 h-4 text-gray-400 cursor-help" />
							</span>
						</h3>
						<p class="p-2 text-sm text-gray-600 leading-relaxed">
							{{ $t("Routing.Messages.MatchColumnSelectionRoutingP2") }}
						</p>
					</div>
					<div v-if="validationErrors.length > 0" class="validation-summary mb-4 p-4">
						<div class="flex items-start gap-3">
							<feather-icon icon="AlertTriangleIcon" class="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
							<div>
								<h4 class="text-sm font-medium text-red-700">
									{{ validationErrors.length }} {{ $t("UploadFiles.RequiredFields") }}
								</h4>
								<ul class="mt-1 text-xs text-red-600">
									<li v-for="error in validationErrors" :key="error" class="text-slate-600">
										{{ error }}
									</li>
								</ul>
							</div>
						</div>
					</div>
					<vue-perfect-scrollbar class="mapping-scroll-area" :settings="settings">
						<!-- Enhanced Field Mapping Cards -->
						<div class="space-y-4">
							<div
								v-for="key in required_keys"
								:key="key"
								class="field-mapping-card transition-all duration-200 hover:shadow-md"
								:class="getFieldCardClass(key)"
							>
								<div class="field-header flex items-center justify-between mb-2">
									<div class="flex items-center">
										<feather-icon
											:icon="getFieldIcon(key)"
											class="w-5 h-5 text-primary cursor-help hover:text-primary-dark font-bold"
											:class="getFieldIconClass(key)"
										/>
										<div>
											<label
												class="field-label text-lg font-bold text-primary flex items-center gap-2 px-2 py-2 rounded-md border-l-4 border-primary"
											>
												{{ resolvePoliticalDivisionName(key) | capitalize }}
												<span
													v-if="isFieldRequired(key)"
													class="required-indicator text-red-500 font-bold"
													>*</span
												>
												<feather-icon
													v-if="getFieldDescription(key)"
													:title="getFieldDescription(key)"
													icon="InfoIcon"
													class="w-5 h-5 cursor-pointer text-primary cursor-help hover:text-primary-dark transition-colors"
												/>
											</label>
										</div>
									</div>
									<!-- Field Status -->
									<div class="field-status">
										<vs-chip
											v-if="isFieldMapped(key)"
											color="success"
											class="text-emerald-800 text-xs font-semibold"
										>
											<feather-icon icon="CheckCircleIcon" class="w-4 h-4 mr-1" />
											{{ $t("UploadFiles.Mapping") }}
										</vs-chip>
										<vs-chip
											v-else-if="isFieldRequired(key)"
											color="danger"
											class="text-emerald-800 text-xs font-semibold"
										>
											<feather-icon icon="AlertCircleIcon" class="w-4 h-4 mr-1" />
											{{ $t("UploadFiles.Required") }}
										</vs-chip>
										<vs-chip v-else color="warning" class="text-emerald-800 text-xs font-semibold">
											<feather-icon icon="MinusCircleIcon" class="w-4 h-4 mr-1" />
											{{ $t("UploadFiles.Optional") }}
										</vs-chip>
									</div>
								</div>
								<!-- Standard Field Selector -->
								<div class="standard-field-container">
									<v-select
										class="enhanced-select"
										v-model="equivalent_fields[key]"
										:clearable="true"
										:options="equivalentFieldsFefault"
										:dir="$vs.rtl ? 'rtl' : 'ltr'"
										:value="v_select_selected"
										@input="onFieldChange(key, $event)"
										:multiple="isSelectMultiple(key)"
										:placeholder="getFieldPlaceholder(key)"
										:searchable="true"
									>
										<template v-slot:no-options="{search, searching}">
											<div class="no-options-container p-3 text-center">
												<feather-icon
													icon="SearchIcon"
													class="w-8 h-8 text-gray-300 mx-auto mb-2"
												/>
												<template v-if="searching">
													<p class="text-sm text-gray-500">
														{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
													</p>
												</template>
												<template v-else>
													<p class="text-sm text-gray-500">
														{{ $t("UploadFiles.NoColumnsAvailable") }}
													</p>
												</template>
											</div>
										</template>
									</v-select>
									<div v-if="getSmartSuggestions(key).length > 0" class="smart-suggestions mt-2">
										<p
											class="suggestions-header flex items-center gap-1 text-sm font-medium text-gray-700 mb-1"
										>
											<span>{{ $t("UploadFiles.SmartSuggestions") }}</span>
											<span class="text-xs text-gray-500 font-normal">
												{{ "(" + $t("UploadFiles.ClickToSelect") + ")" }}
											</span>
										</p>
										<div class="flex flex-wrap gap-1">
											<button
												v-for="suggestion in getSmartSuggestions(key)"
												:key="suggestion"
												:class="getSuggestionChipClass(suggestion, key)"
												class="suggestion-chip cursor-pointer px-2 py-1 text-xs rounded-full transition-all duration-300"
												@click="applySuggestion(key, suggestion)"
												:disabled="equivalent_fields[key] === suggestion"
											>
												{{ suggestion }}
											</button>
										</div>
									</div>
								</div>
							</div>
						</div>
					</vue-perfect-scrollbar>
				</div>

				<!-- Enhanced Data Preview Section -->
				<div class="preview-section">
					<div class="section-header">
						<h3 class="text-xl font-semibold text-gray-800 flex items-center gap-3">
							<div class="icon-wrapper p-2 bg-green-100 rounded-lg">
								<feather-icon icon="EyeIcon" class="w-5 h-5 text-green-600" />
							</div>
							{{ $t("Routing.Title.TableExample") }}
							<div class="preview-stats flex items-center gap-4 ml-auto">
								<div class="stat-item">
									<span class="text-xs text-gray-500">{{ $t("UploadFiles.Records") }}:</span>
									<span class="text-sm font-semibold text-gray-700">{{ uploadRecords.length }}</span>
								</div>
								<div class="stat-item">
									<span class="text-xs text-gray-500">{{ $t("UploadFiles.Columns") }}:</span>
									<span class="text-sm font-semibold text-gray-700">{{ headers.length }}</span>
								</div>
							</div>
						</h3>
						<p class="p-2 text-sm text-gray-600 leading-relaxed">
							{{ $t("Routing.Title.TableExampleText") }}
						</p>
					</div>

					<!-- Enhanced Data Table -->
					<div class="data-preview-container">
						<vue-perfect-scrollbar class="enhanced-table-scroll" :settings="settings">
							<div class="overflow-x-auto">
								<table class="enhanced-data-table w-full">
									<caption></caption>
									<thead>
										<tr class="table-header">
											<th
												v-for="header in headers"
												:key="header"
												class="header-cell"
												:class="getHeaderClass(header)"
											>
												<div class="header-content">
													<span class="header-text">{{ header | mayus }}</span>
													<div v-if="isHeaderMapped(header)" class="mapping-indicator">
														<feather-icon icon="LinkIcon" class="w-3 h-3 text-green-500" />
													</div>
												</div>
											</th>
										</tr>
									</thead>
									<tbody>
										<tr
											v-for="(record, index) in previewRecords"
											:key="`record-${index}`"
											class="table-row"
										>
											<td
												v-for="header in headers"
												:key="`cell-${index}-${header}`"
												class="table-cell"
												:class="getCellClass(header, record[header])"
											>
												<div class="cell-content">
													<span class="cell-text">{{ formatCellValue(record[header]) }}</span>
													<div v-if="isCellEmpty(record[header])" class="empty-indicator">
														<feather-icon
															icon="AlertCircleIcon"
															class="w-3 h-3 text-amber-400"
														/>
													</div>
												</div>
											</td>
										</tr>
										<!-- Skeleton Placeholder Rows -->
										<template v-if="placeholderRowCount > 0">
											<tr
												v-for="n in placeholderRowCount"
												:key="`placeholder-row-${n}`"
												class="table-row is-placeholder"
											>
												<td
													v-for="header in headers"
													:key="`placeholder-cell-${n}-${header}`"
													class="table-cell"
												>
													<div class="skeleton-placeholder"></div>
												</td>
											</tr>
										</template>
									</tbody>
								</table>
							</div>
						</vue-perfect-scrollbar>
					</div>
				</div>
			</div>
		</div>

		<!-- Sticky Action Bar -->
		<div class="sticky-actions-wrapper" ref="stickyActions">
			<div class="flex flex-col sm:flex-row gap-3 justify-end">
				<vs-button
					class="cancel-btn"
					type="border"
					color="danger"
					icon-pack="feather"
					icon="icon-x"
					size="small"
					@click.prevent="handleCancel"
				>
					{{ $t("Cancel") }}
				</vs-button>

				<vs-button
					class="save-btn"
					color="primary"
					icon-pack="feather"
					icon="icon-save"
					size="small"
					:loading="isProcessing"
					@click="validateUploadEquivalents"
				>
					{{ $t("Save") }}
				</vs-button>
			</div>
		</div>

		<!-- Popup Alerts -->
		<vs-popup style="z-index: 54000" :title="popupTitle" :active.sync="popupActiveAlert">
			<alert size="small" :title="alert_title" :message="alert_message" :color="alert_color"></alert>
		</vs-popup>
		<vs-popup :title="$t('Routing.Title.MatchColumn')" :active.sync="equivalentDataErrorsAlert">
			<vs-alert icon="warning" :active="equivalentDataErrorsAlert" color="danger" class="my-3">
				<span>{{ $t("ManualData.ErrorsFound") }}.</span>
			</vs-alert>

			<vue-perfect-scrollbar class="scroll-area-table" :settings="settings">
				<vs-table :data="equivalentDataErrors">
					<template slot="thead">
						<vs-th>{{ $t("Register") }} N</vs-th>
						<vs-th>{{ $t("Error") }}</vs-th>
					</template>

					<template slot-scope="{data}">
						<vs-tr :key="indextr" v-for="(tr, indextr) in data">
							<vs-td :data="data[indextr].index">
								{{ data[indextr].index + 1 }}
							</vs-td>
							<vs-td :data="data[indextr].index">
								<span>{{ $t("ManualData.PropertyError") }} </span>
								<span class="font-semibold"
									>{{ identifyPropertyName(data[indextr].message) | title }},
								</span>
								<span> {{ identifyMessageError(data[indextr].message) }}</span>
							</vs-td>
						</vs-tr>
					</template>
				</vs-table>
			</vue-perfect-scrollbar>
		</vs-popup>
	</div>
</template>

<script>
import vSelect from "vue-select";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import Commons from "@/helpers/commons.js";
import Alert from "@/components/alerts/Alert.vue";
import FloatingButtons from "@/views/FloatingButtons.vue";

export default {
	components: {
		vSelect,
		Alert,
		VuePerfectScrollbar,
		FloatingButtons,
	},
	props: {
		customConfig: {
			type: Object,
			default: () => {
				return {
					actionDropdown: true,
					btnAssigGroup: false,
					columns: ["name", "description", "address", "hour_init", "hour_end", "action"],
				};
			},
		},
	},
	data() {
		return {
			selected: [],
			itemsPerPage: 5,
			isMounted: false,
			addNewDataSidebar: false,
			sidebarData: {},
			pagination: {
				next_page: 0,
				total_pages: 0,
				total_records: 0,
			},
			currentPageLocal: 1,
			quantityElementPage: 10,
			groupDelete: null,
			equivalentOptions: [
				"cx",
				"cy",
				"identification",
				"demand",
				"end_hour_window",
				"init_hour_window",
				"skills",
				"time_service",
				"add",
				"null",
			],
			equivalent_fields_default_selected: [],
			equivalent_fields: {},
			equivalent_fields_result: null,
			v_select_selected: "add",
			popupActiveAlert: false,
			alert_title: "",
			alert_message: "",
			alert_color: "",
			popupTitle: "",
			settings: {
				maxScrollbarLength: 60,
				wheelSpeed: 0.6,
			},
			equivalentDataErrors: [],
			equivalentDataErrorsAlert: false,

			// Enhanced UX/UI properties
			isProcessing: false,
			validationErrors: [],
			showHeaderButtons: false,
			intersectionObserver: null,
			dropdownObserver: null,
			fieldDescriptions: {
				identification: this.$t("FieldDescriptions.identification") || "Campo de identificación único",
				cx: this.$t("FieldDescriptions.longitude") || "Coordenada de longitud",
				cy: this.$t("FieldDescriptions.latitude") || "Coordenada de latitud",
				demand: this.$t("FieldDescriptions.demand") || "Demanda del punto",
				skills: this.$t("FieldDescriptions.skills") || "Habilidades requeridas",
				time_service: this.$t("FieldDescriptions.time_service") || "Tiempo de servicio",
				init_depot_id:
					this.$t("FieldDescriptions.init_depot_id") || "Coordenada de latitud del depósito inicial",
				end_depot_id: this.$t("FieldDescriptions.end_depot_id") || "Coordenada de latitud del depósito final",
				additional_info: this.$t("FieldDescriptions.additional_info") || "Información adicional",
				distance_cost: this.$t("FieldDescriptions.distance_cost") || "Costo de distancia",
				fixed_cost: this.$t("FieldDescriptions.fixed_cost") || "Costo fijo",
				max_travel_distance: this.$t("FieldDescriptions.max_travel_distance") || "Distancia máxima de viaje",
				max_travel_duration: this.$t("FieldDescriptions.max_travel_duration") || "Tiempo máximo de viaje",
				time_cost: this.$t("FieldDescriptions.time_cost") || "Costo por hora",
				traveled_hour_cost: this.$t("FieldDescriptions.traveled_hour_cost") || "Costo por hora de viaje",
				profile: this.$t("FieldDescriptions.profile") || "Perfil de vehículo",
				address: this.$t("FieldDescriptions.address") || "Dirección",
				country: this.$t("FieldDescriptions.country") || "País",
				status: this.$t("FieldDescriptions.status") || "Estado",
				priority: this.$t("FieldDescriptions.priority") || "Prioridad",
				department: this.$t("FieldDescriptions.department") || "Departamento",
				city: this.$t("FieldDescriptions.city") || "Ciudad",
			},
			fieldHints: {
				identification: this.$t("FieldHints.identification") || "Identificador único para cada registro",
				cx: this.$t("FieldHints.longitude") || "Valor decimal de longitud",
				cy: this.$t("FieldHints.latitude") || "Valor decimal de latitud",
				demand: this.$t("FieldHints.demand") || "Cantidad numérica de demanda",
			},
		};
	},
	computed: {
		currentPage: {
			get() {
				return this.currentPageLocal;
			},
			set(val) {
				this.currentPageLocal = val;
			},
		},
		totalPages() {
			if (this.pagination.total_pages > 0) return this.pagination.total_pages;
			else return 0;
		},
		uploadRecords() {
			return this.$store.state.uploadFile.currentUpload.records;
		},
		headers() {
			return this.$store.state.uploadFile.currentUpload.keys;
		},
		required_keys() {
			const requiredKeys = Object.keys(this.$store.state.uploadFile.currentUpload.json_base).map((el) => {
				return el;
			});
			return requiredKeys;
		},
		validateJsonBase() {
			return (category) => {
				let json_base = {};
				if (category == "VEHICLE_MASSIVE") {
					json_base = {identification: null};
				} else if (category == "VISIT_POINT_MASSIVE") {
					json_base = {
						cx: null,
						cy: null,
						identification: null,
					};
				}
				return json_base;
			};
		},
		resolvePoliticalDivisionName() {
			return (el) => {
				if (el === "department") return "dpto / state / prov / region";
				if (el === "city") return "city / mun / comu / cton / dsto";
				return el;
			};
		},
		isSelectMultiple() {
			return function (key) {
				return Array.isArray(this.$store.state.uploadFile.currentUpload.json_base[key]);
			};
		},
		equivalent_options() {
			return this.$store.state.uploadFile.currentUpload.keys.map((el) => {
				return el;
			});
		},
		queriedItems() {
			return this.$refs.table ? this.$refs.table.queriedResults.length : this.uploadRecords.length;
		},
		equivalentFieldsFefault: {
			get() {
				return this.equivalent_options.filter(
					(e) => !this.equivalent_fields_default_selected.some((eq) => eq === e)
				);
			},
			set(val) {},
		},
		identifyPropertyName() {
			return function (message) {
				return message.split(" ")[0];
			};
		},
		identifyMessageError() {
			return function (message) {
				return this.findText("has an invalid value", message) ? this.$t("ManualData.InvalidValue") : message;
			};
		},
		previewRecords() {
			return this.uploadRecords;
		},
		mappedFieldsCount() {
			return Object.values(this.equivalent_fields).filter(
				(value) => value !== null && value !== "" && value !== undefined
			).length;
		},
		totalRequiredFields() {
			return this.required_keys.length;
		},
		mappingCompletionPercentage() {
			return this.totalRequiredFields > 0
				? Math.round((this.mappedFieldsCount / this.totalRequiredFields) * 100)
				: 0;
		},
		placeholderRowCount() {
			const minRows = 13;
			if (!this.uploadRecords || this.uploadRecords.length >= minRows) {
				return 0;
			}
			return minRows - this.uploadRecords.length;
		},
	},
	methods: {
		setSelected(value) {
			this.setEquivalents();
		},
		setEquivalents() {
			let equivalentFields = {};
			for (const property in this.equivalent_fields) {
				const value = this.equivalent_fields[property];
				if (value !== "null") {
					if (value === "add") {
						equivalentFields[property] = property;
					} else {
						equivalentFields[property] = value;
					}
				}
			}
			this.equivalent_fields_result = {...equivalentFields};
			this.equivalent_fields_default_selected = Object.values(equivalentFields);
			this.$store.commit("uploadFile/SET_EQUIVALENTS", this.equivalent_fields_result);
		},
		openLoading() {
			this.$vs.loading();
		},
		closeLoading() {
			this.$vs.loading.close();
		},
		enableHeadTable(headName) {
			return this.customConfig.columns.includes(headName);
		},
		addNewData() {
			this.sidebarData = {};
			this.toggleDataSidebar(true);
		},
		editData(data) {
			this.$emit("selectedAlarmEmit", data);
		},
		toggleDataSidebar(val = false) {
			this.addNewDataSidebar = val;
		},
		cahngeCurrentPageLocal() {
			this.currentPageLocal = 1;
		},
		validateUploadEquivalents() {
			if (!this.validateRequiredKeys()) {
				this.alert_title = this.$t("Routing.Messages.MatchColumnSelectionRoutingErrorTitle");
				this.alert_message = `${this.$t(
					"Routing.Messages.MatchColumnSelectionRoutingError"
				)} ${this.missingRequiredKeys().join(", ")}`;
				this.alert_color = "warning";
				this.popupTitle = this.$t("Routing.Buttons.Validate");
				this.popupActiveAlert = true;
				return;
			}
			Object.keys(this.$store.state.uploadFile.equivalents).forEach((key) => {
				if (this.$store.state.uploadFile.equivalents[key] === null)
					delete this.$store.state.uploadFile.equivalents[key];
			});
			let equivalents = {
				equivalent_fields: this.$store.state.uploadFile.equivalents || {},
			};
			let payload = {
				id: this.$store.state.uploadFile.currentUpload.id,
				body: equivalents,
			};
			const {category} = this.$store.getters["uploadFile/uploadedInfo"];
			const urlUploadValidate =
				category === "VEHICLE_MASSIVE"
					? "uploadFile/uploadValidateVehicle"
					: "uploadFile/uploadValidateVisitPoint";
			this.openLoading();
			this.$store
				.dispatch(urlUploadValidate, payload)
				.then((res) => {
					this.$emit("closeEquivalenceEmit");
					this.closeLoading();
				})
				.catch((error) => {
					this.closeLoading();
					if (error) {
						if (Array.isArray(error.data)) {
							this.manageArrayErrors(error.data);
						} else {
							this.manageErrorAlert(error);
						}
					} else {
						if (error.message === "missing fields: ['equivalent_fields']") {
							this.alert_title = this.$t("Routing.Messages.MatchColumnSelectionRoutingErrorTitle");
							this.alert_message = `${this.$t(
								"Routing.Messages.MatchColumnSelectionRoutingErrorEquivalents"
							)}`;
							this.alert_color = "danger";
							this.popupTitle = this.$t("Error");
							this.popupActiveAlert = true;
						}
					}
				});
		},
		manageErrorAlert(err) {
			const findErrorNameProperty = Commons.findErrorNameProperty();
			const nameProperty = findErrorNameProperty(err);
			this.alert_title = this.$t("Routing.Messages.MatchColumnSelectionRoutingErrorTitle");
			this.alert_message = `${this.$t(
				"Routing.Messages.MatchColumnSelectionRoutingError"
			)} ${nameProperty.toUpperCase()}`;
			this.alert_color = "danger";
			this.popupTitle = this.$t("Error");
			this.popupActiveAlert = true;
		},
		validateRequiredKeys() {
			const assignedRequiredKeys = this.$store.state.uploadFile.equivalents || {};
			const {category} = this.$store.getters["uploadFile/uploadedInfo"];
			const keys1 = Object.keys(this.validateJsonBase(category));
			const keys2 = Object.keys(assignedRequiredKeys);
			const haveSameProperties = keys1.every((key) => keys2.includes(key) && assignedRequiredKeys[key]);
			return haveSameProperties;
		},
		missingRequiredKeys() {
			const assignedRequiredKeys = this.$store.state.uploadFile.equivalents || {};
			const {category} = this.$store.getters["uploadFile/uploadedInfo"];
			const keys1 = Object.keys(this.validateJsonBase(category));
			return keys1.filter((key) => !assignedRequiredKeys[key]);
		},

		manageArrayErrors(data = []) {
			this.equivalentDataErrors = data;
			this.equivalentDataErrorsAlert = true;
		},
		findText(searchStr, str) {
			return str.search(searchStr) !== -1 ? true : false;
		},
		/**
		 * Automatically assigns equivalent fields based on the keys present in the current upload.
		 * @method autoEquivalents
		 * @returns {void}
		 */
		autoEquivalents() {
			const keys = [...this.$store.state.uploadFile.currentUpload.keys];
			// check if keys are in the object and assign them to the equivalent_fields
			for (const key of this.required_keys) {
				if (keys.includes(key)) {
					this.equivalent_fields[key] = key;
				}
			}
		},

		// Enhanced UX/UI Methods
		setupIntersectionObserver() {
			// Create intersection observer to watch the sticky actions
			this.intersectionObserver = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						// Show header buttons when footer buttons are not fully visible
						this.showHeaderButtons = !entry.isIntersecting;
					});
				},
				{
					threshold: 0.1, // Trigger when 10% of the element is visible
					rootMargin: "0px 0px -50px 0px", // Add some margin to trigger earlier
				}
			);

			// Start observing the sticky actions element
			if (this.$refs.stickyActions) {
				this.intersectionObserver.observe(this.$refs.stickyActions);
			}
		},

		isFieldRequired(key) {
			const {category} = this.$store.getters["uploadFile/uploadedInfo"];
			const requiredFields = Object.keys(this.validateJsonBase(category));
			return requiredFields.includes(key);
		},

		isFieldMapped(key) {
			return (
				this.equivalent_fields[key] &&
				this.equivalent_fields[key] !== "" &&
				this.equivalent_fields[key] !== null
			);
		},

		getFieldCardClass(key) {
			const baseClass = "p-4 border rounded-lg";
			if (this.isFieldMapped(key)) {
				return `${baseClass} border-green-200 bg-green-50/50`;
			} else if (this.isFieldRequired(key)) {
				return `${baseClass} border-red-200 bg-red-50/50`;
			}
			return `${baseClass} border-gray-200 bg-gray-50/30`;
		},

		getFieldIconClass(key) {
			if (this.isFieldMapped(key)) {
				return "text-primary";
			} else if (this.isFieldRequired(key)) {
				return "bg-red-100 text-red-600";
			}
			return "bg-gray-100 text-gray-600";
		},

		getFieldIcon(key) {
			const iconMap = {
				identification: "TagIcon",
				priority: "StarIcon",
				time_service: "ClockIcon",
				skills: "ToolIcon",
				demand: "TrendingUpIcon",
				allowed_vehicles: "TruckIcon",
				time_window: "CalendarIcon",
				additional_info: "InfoIcon",
				territory_id: "MapIcon",
				profile: "UserIcon",
				max_travel_distance: "CornerRightUpIcon",
				max_travel_duration: "ClockIcon",
				init_depot: "HomeIcon",
				end_depot: "FlagIcon",
				capacity: "PackageIcon",
				breaks: "PauseIcon",
				time_cost: "DollarSignIcon",
				traveled_hour_cost: "DollarSignIcon",
				distance_cost: "DollarSignIcon",
				fixed_cost: "DollarSignIcon",
				strict_territories: "LockIcon",
				territories: "MapIcon",
				original_identification: "LinkIcon",
				cx: "MapPinIcon",
				cy: "MapPinIcon",
				status: "CheckCircleIcon",
				address: "ZapIcon",
				country: "FlagIcon",
				department: "NavigationIcon",
				city: "Navigation2Icon",
				end_depot_id: "MapPinIcon",
				init_depot_id: "MapPinIcon",
			};
			return iconMap[key] || "CircleIcon";
		},

		getFieldDescription(key) {
			return this.fieldDescriptions[key] || "";
		},

		getFieldHint(key) {
			return this.fieldHints[key] || "";
		},

		getFieldPlaceholder(key) {
			if (this.isSelectMultiple(key)) {
				return this.$t("FieldHints.select_multiple", {key: this.resolvePoliticalDivisionName(key)});
			}
			return this.$t("FieldHints.select_single", {key: this.resolvePoliticalDivisionName(key)});
		},

		onFieldChange(key, value) {
			this.setSelected();
			this.updateValidationErrors();
			// Forzar actualización de las sugerencias después de mapear un campo
			this.$nextTick(() => {
				this.$forceUpdate(); // Esto hará que se recalculen las sugerencias
			});
		},

		updateValidationErrors() {
			this.validationErrors = this.missingRequiredKeys();
		},

		getSmartSuggestions(key) {
			const suggestions = [];
			const headers = this.headers;
			// Obtener todas las columnas que ya están siendo usadas en otros campos
			const usedColumns = new Set();
			Object.keys(this.equivalent_fields).forEach((fieldKey) => {
				if (fieldKey !== key && this.equivalent_fields[fieldKey]) {
					// Si es array (campos múltiples), agregar todos los elementos
					if (Array.isArray(this.equivalent_fields[fieldKey])) {
						this.equivalent_fields[fieldKey].forEach((col) => {
							if (col) usedColumns.add(col);
						});
					} else {
						// Si es string simple, agregarlo directamente
						usedColumns.add(this.equivalent_fields[fieldKey]);
					}
				}
			});
			// Smart matching based on field name similarity
			const fieldPatterns = {
				identification: ["id", "identifier", "codigo", "code"],
				cx: ["lng", "lon", "longitud", "x"],
				cy: ["lat", "latitud", "y"],
				demand: ["demanda", "cantidad", "volumen", "peso"],
				skills: ["habilidad", "skill", "competencia"],
				time_service: ["tiempo", "time", "duracion", "duration"],
			};
			if (fieldPatterns[key]) {
				headers.forEach((header) => {
					const headerLower = header.toLowerCase();
					fieldPatterns[key].forEach((pattern) => {
						// Solo agregar si la columna no está ya siendo usada en otro campo
						// Y no está ya en las sugerencias
						if (
							headerLower.includes(pattern) &&
							!suggestions.includes(header) &&
							!usedColumns.has(header)
						) {
							suggestions.push(header);
						}
					});
				});
			}
			return suggestions.slice(0, 3); // Maximum 3 suggestions
		},
		debugUsedColumns() {
			const usedColumns = new Set();
			const mappedFields = {};

			Object.keys(this.equivalent_fields).forEach((fieldKey) => {
				if (this.equivalent_fields[fieldKey]) {
					mappedFields[fieldKey] = this.equivalent_fields[fieldKey];

					if (Array.isArray(this.equivalent_fields[fieldKey])) {
						this.equivalent_fields[fieldKey].forEach((col) => {
							if (col) usedColumns.add(col);
						});
					} else {
						usedColumns.add(this.equivalent_fields[fieldKey]);
					}
				}
			});
		},
		applySuggestion(key, suggestion) {
			if (this.isSelectMultiple(key)) {
				if (Array.isArray(this.equivalent_fields[key]) && this.equivalent_fields[key].length > 0) {
					this.equivalent_fields[key].push(suggestion);
				} else {
					this.equivalent_fields[key] = [suggestion];
				}
			} else {
				this.equivalent_fields[key] = suggestion;
			}
			this.onFieldChange(key, this.equivalent_fields[key]);
			setTimeout(() => {
				this.debugUsedColumns();
			}, 100);
		},
		getSuggestionChipClass(suggestion, key) {
			const isSelected = this.equivalent_fields[key] === suggestion;
			const baseClass = "group relative";

			if (isSelected) {
				return `${baseClass} bg-gradient-to-r from-emerald-500 to-emerald-600 text-white border-emerald-600 shadow-md line-through opacity-75 cursor-not-allowed`;
			}
			return `${baseClass} bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border border-blue-200 hover:from-blue-100 hover:to-indigo-100 hover:border-blue-300 hover:shadow-md`;
		},
		getHeaderClass(header) {
			return this.isHeaderMapped(header) ? "mapped-header" : "";
		},

		isHeaderMapped(header) {
			return Object.values(this.equivalent_fields).includes(header);
		},

		getCellClass(header, value) {
			const baseClass = "relative";
			if (this.isCellEmpty(value)) {
				return `${baseClass} empty-cell`;
			}
			if (this.isHeaderMapped(header)) {
				return `${baseClass} mapped-cell`;
			}
			return baseClass;
		},

		formatCellValue(value) {
			if (value === null || value === undefined || value === "") {
				return "—";
			}
			if (typeof value === "string" && value.length > 20) {
				return value.substring(0, 20) + "...";
			}
			return value;
		},

		isCellEmpty(value) {
			return value === null || value === undefined || value === "";
		},

		handleCancel() {
			this.$emit("cancelEquivalenceEmit");
		},

		// Intelligent dropdown positioning
		setupIntelligentDropdowns() {
			// Use MutationObserver to watch for dynamically added v-select elements
			const observer = new MutationObserver((mutations) => {
				mutations.forEach((mutation) => {
					mutation.addedNodes.forEach((node) => {
						if (node.nodeType === 1) {
							// Element node
							this.attachDropdownListeners(node);
						}
					});
				});
			});

			observer.observe(this.$el, {
				childList: true,
				subtree: true,
			});

			// Setup initial dropdowns
			this.$nextTick(() => {
				this.attachDropdownListeners(this.$el);
			});

			// Store observer for cleanup
			this.dropdownObserver = observer;
		},

		attachDropdownListeners(container) {
			const vSelects = container.querySelectorAll
				? container.querySelectorAll(".v-select")
				: container.classList && container.classList.contains("v-select")
				? [container]
				: [];

			vSelects.forEach((select) => {
				// Avoid duplicate listeners
				if (select.dataset.dropdownSetup) return;
				select.dataset.dropdownSetup = "true";

				const dropdown = select.querySelector(".vs__dropdown-toggle");
				if (dropdown) {
					const handleClick = () => {
						// Use a small delay to ensure dropdown is rendered
						setTimeout(() => {
							this.adjustDropdownPosition(select);
						}, 10);
					};

					dropdown.addEventListener("click", handleClick);

					// Also handle focus events for keyboard navigation
					dropdown.addEventListener("focus", handleClick);
				}
			});
		},

		adjustDropdownPosition(selectElement) {
			if (!selectElement) return;

			const selectRect = selectElement.getBoundingClientRect();
			const viewportHeight = window.innerHeight;
			const dropdownHeight = 350; // max-height from CSS
			const spaceBelow = viewportHeight - selectRect.bottom;
			const spaceAbove = selectRect.top;
			const scrollBuffer = 20; // Extra buffer for scroll

			// Remove existing positioning classes
			selectElement.classList.remove("vs--above", "vs--below");

			// Determine if dropdown should open upward or downward
			if (spaceBelow < dropdownHeight + scrollBuffer && spaceAbove > spaceBelow) {
				selectElement.classList.add("vs--above");
			} else {
				selectElement.classList.add("vs--below");
			}
		},
	},
	filters: {
		mayus(val = "") {
			return val.toUpperCase();
		},
	},
	watch: {
		itemsPerPage() {
			this.currentPageLocal = 1;
		},
	},
	created() {
		this.equivalent_fields = {...this.$store.state.uploadFile.equivalents};
		if (Object.values(this.equivalent_fields).length > 0) {
			this.setEquivalents();
		} else {
			const required_keys = {...this.$store.state.uploadFile.currentUpload.required_keys};
			for (const key in required_keys) {
				this.equivalent_fields[key] = "";
			}
			this.autoEquivalents();
			this.setEquivalents();
		}
	},
	mounted() {
		this.isMounted = true;
		this.setupIntersectionObserver();
		this.setupIntelligentDropdowns();
	},
	beforeDestroy() {
		if (this.intersectionObserver) {
			this.intersectionObserver.disconnect();
		}
		if (this.dropdownObserver) {
			this.dropdownObserver.disconnect();
		}
	},
};
</script>

<style lang="scss" scoped>
// Enhanced styles for modern UX/UI
.equivalences-container {
	@apply max-w-full mx-auto;
	font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

// Primary color utilities (using VS primary variables)
.text-primary {
	color: rgba(var(--vs-primary), 1) !important;
}

.text-primary-dark {
	color: rgba(var(--vs-primary), 0.8) !important;
}

.border-primary {
	border-color: rgba(var(--vs-primary), 1) !important;
}

// Minimal Glass Container Effect
.glass-container {
	background: rgba(255, 255, 255, 0.95);
	backdrop-filter: blur(8px);
	border: 1px solid rgba(226, 232, 240, 0.8);
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02);
}

// Section Headers
.section-header {
	h3 {
		font-weight: 600;
		letter-spacing: -0.025em;
	}

	.icon-wrapper {
		border-radius: 0.75rem;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
	}
}

// Minimal Field Mapping Cards
.field-mapping-card {
	background: rgba(255, 255, 255, 0.9);
	border-radius: 0.5rem;
	border: 1px solid #f1f5f9;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
	transition: all 0.2s ease;

	&:hover {
		box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
		border-color: #e2e8f0;
	}

	.field-icon {
		border-radius: 0.5rem;
		transition: all 0.2s ease;
	}

	.field-label {
		font-weight: 500;
		color: #374151;
	}

	.required-indicator {
		font-size: 0.875rem;
		font-weight: 700;
	}

	.field-hint {
		color: #6b7280;
		font-style: italic;
	}

	.status-indicator {
		font-weight: 500;
		border-radius: 9999px;
		font-size: 0.75rem;
		letter-spacing: 0.025em;
		box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
	}
}

// Global v-select dropdown styles with intelligent positioning
::v-deep .vs__dropdown-menu {
	z-index: 9999 !important;
	display: block;
	-webkit-box-sizing: border-box;
	box-sizing: border-box;
	position: absolute;
	width: 100%;
	max-height: 350px;
	min-width: 160px;
	overflow-y: auto;
	-webkit-box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.15);
	box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.15);
	list-style: none;
	background: rgba(255, 255, 255, 0.95);
	backdrop-filter: blur(20px);
	border: 1px solid rgba(255, 255, 255, 0.2);
	border-radius: 0.75rem;
}

// Dropdown opens upward when there's no space below
::v-deep .vs--open.vs--below .vs__dropdown-menu {
	top: calc(100% - 1px);
	bottom: auto;
	margin-top: 4px;
	margin-bottom: 0;
	transform-origin: top center;
	animation: dropdownSlideDown 0.15s ease-out;
}

::v-deep .vs--open.vs--above .vs__dropdown-menu {
	bottom: calc(100% - 1px);
	top: auto;
	margin-top: 0;
	margin-bottom: 4px;
	transform-origin: bottom center;
	animation: dropdownSlideUp 0.15s ease-out;
}

// Smooth animations for dropdown appearance
@keyframes dropdownSlideDown {
	from {
		opacity: 0;
		transform: translateY(-8px) scaleY(0.95);
	}
	to {
		opacity: 1;
		transform: translateY(0) scaleY(1);
	}
}

@keyframes dropdownSlideUp {
	from {
		opacity: 0;
		transform: translateY(8px) scaleY(0.95);
	}
	to {
		opacity: 1;
		transform: translateY(0) scaleY(1);
	}
}

// Enhanced Select Styles
.enhanced-select-wrapper {
	@apply relative flex items-end gap-2;

	.enhanced-select {
		@apply flex-1;

		::v-deep .vs__dropdown-toggle {
			background: rgba(255, 255, 255, 0.9);
			border: 1.5px solid #e5e7eb;
			border-radius: 0.75rem;
			min-height: 48px;
			transition: all 0.2s ease;

			&:hover {
				border-color: #6366f1;
				box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
			}
		}

		::v-deep .vs__option {
			padding: 12px 16px;
			transition: all 0.15s ease;
			color: #374151;

			&--highlight {
				background: #f1f5f9;
				color: #1e293b;
			}
		}
	}
}

.smart-suggestions {
	.suggestions-header {
		color: #505050;
		font-style: italic;
	}
	.suggestion-chip {
		transition: all 0.2s ease;
		font-weight: 700;
		background: rgba(var(--vs-primary), 1);
		color: #f1f5f9;
		border: 1px solid #e2e8f0;
		&:hover {
			background-color: rgba(var(--vs-primary), 0.7);
			border-color: #cbd5e1;
			box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
		}
	}
}

// No Options Container
.no-options-container {
	padding: 1.5rem;
	text-align: center;

	.feather-icon {
		opacity: 0.6;
	}
}

// Enhanced Data Table
.enhanced-data-table {
	border-collapse: separate;
	border-spacing: 0;
	background: rgba(255, 255, 255, 0.9);
	border-radius: 0.75rem;
	overflow: hidden;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

	.table-header {
		background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
		border-bottom: 2px solid #e2e8f0;

		.header-cell {
			padding: 1rem;
			text-align: left;
			font-weight: 600;
			color: #374151;
			font-size: 0.875rem;
			letter-spacing: 0.025em;
			position: relative;

			&.mapped-header {
				background: linear-gradient(135deg, #ecfdf5 0%, #ccc 100%);
				color: rgba(var(--vs-primary), 1);
				border-bottom: 2px solid #888484;
			}

			.header-content {
				@apply flex items-center justify-between;

				.mapping-indicator {
					@apply ml-2;
				}
			}
		}
	}

	.table-row {
		transition: all 0.15s ease;

		&:hover {
			background-color: rgba(99, 102, 241, 0.05);
		}

		&:nth-child(even) {
			background-color: rgba(248, 250, 252, 0.8);
		}

		&.is-placeholder {
			&:hover,
			&:nth-child(even) {
				background-color: transparent;
			}
		}

		.table-cell {
			padding: 0.875rem 1rem;
			border-bottom: 1px solid rgba(229, 231, 235, 0.6);
			font-size: 0.875rem;

			&.mapped-cell {
				background-color: rgba(16, 185, 129, 0.05);
				border-left: 3px solid #888484;
			}

			&.empty-cell {
				background-color: rgba(245, 158, 11, 0.05);

				.cell-text {
					color: #9ca3af;
					font-style: italic;
				}
			}

			.cell-content {
				@apply flex items-center justify-between;

				.cell-text {
					@apply truncate;
					max-width: 200px;
				}

				.empty-indicator {
					@apply ml-2 flex-shrink-0;
				}
			}
		}
	}
}

// Minimal Action Buttons
.action-buttons-container {
	.cancel-btn,
	.save-btn {
		border-radius: 0.5rem !important;
		padding: 0.75rem 1.5rem !important;
		font-weight: 500 !important;
		letter-spacing: 0.025em;
		transition: all 0.2s ease !important;
	}

	.cancel-btn {
		background: #f8fafc !important;
		color: #475569 !important;
		border: 1px solid #e2e8f0 !important;

		&:hover {
			background: #f1f5f9 !important;
			border-color: #cbd5e1 !important;
			box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
		}
	}

	.save-btn {
		background: #1e293b !important;
		color: white !important;
		border: 1px solid #1e293b !important;

		&:hover {
			background: #0f172a !important;
			box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
		}
	}
}

// Minimal Validation Summary
.validation-summary {
	border-radius: 0.5rem;
	backdrop-filter: blur(4px);
	animation: slideIn 0.3s ease-out;
	background: #fef2f2;
	border: 1px solid #fecaca;
}

@keyframes slideIn {
	from {
		opacity: 0;
		transform: translateY(-10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

// Scrollbar Styles
.mapping-scroll-area,
.enhanced-table-scroll {
	border-radius: 0.5rem;
	height: 600px; /* Added height for scrollbar */

	::v-deep .ps__rail-y {
		background-color: rgba(0, 0, 0, 0.05);
		border-radius: 0.25rem;

		.ps__thumb-y {
			background-color: rgba(99, 102, 241, 0.3);
			border-radius: 0.25rem;

			&:hover {
				background-color: rgba(99, 102, 241, 0.5);
			}
		}
	}
}

// Preview Stats
.preview-stats {
	.stat-item {
		@apply flex flex-col items-center gap-1;

		span:first-child {
			font-weight: 400;
		}

		span:last-child {
			font-weight: 600;
		}
	}
}

// Responsive Design
@media (max-width: 768px) {
	.equivalences-container {
		padding: 1rem;
	}

	.glass-container {
		padding: 1rem;

		.grid {
			grid-template-columns: 1fr;
			gap: 2rem;
		}
	}

	.field-mapping-card {
		padding: 1rem;
	}

	.preview-stats {
		flex-direction: column;
		gap: 1rem;
		margin-left: 0 !important;
		margin-top: 1rem;
	}

	.enhanced-data-table {
		font-size: 0.75rem;

		.header-cell,
		.table-cell {
			padding: 0.5rem;
		}
	}

	.action-buttons-container {
		.flex {
			flex-direction: column;
		}
	}
}

// High contrast mode support
@media (prefers-contrast: high) {
	.glass-container {
		background: white;
		border: 2px solid #000;
	}

	.field-mapping-card {
		background: white;
		border: 1px solid #000;
	}
}

// Reduced motion support
@media (prefers-reduced-motion: reduce) {
	* {
		animation-duration: 0.01ms !important;
		animation-iteration-count: 1 !important;
		transition-duration: 0.01ms !important;
	}
}

// Dark mode support (if enabled in future)
@media (prefers-color-scheme: dark) {
	.glass-container {
		border-color: rgba(75, 85, 99, 0.3);
	}

	.field-mapping-card {
		border-color: rgba(75, 85, 99, 0.3);
	}
}

// Legacy browser fallbacks
th {
	border: 1px solid #dddddd;
	text-align: left;
	padding: 8px;
}

td {
	border-bottom: 1px solid #dddddd;
	text-align: left;
	padding: 8px;
}

.scroll-area {
	margin: auto;
	width: 100%;
	height: 400px;
}

.scroll-area-table {
	margin: auto;
	width: 100%;
	height: 450px;
}

@keyframes shimmer {
	100% {
		transform: translateX(100%);
	}
}

.skeleton-placeholder {
	width: 100%;
	height: 1rem;
	border-radius: 0.25rem;
	background-color: #e5e7eb;
	position: relative;
	overflow: hidden;

	&::after {
		content: "";
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		transform: translateX(-100%);
		background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
		animation: shimmer 1.5s infinite;
	}
}

.mapping-scroll-area {
	height: 600px; /* Added height for scrollbar */
}

.sticky-actions-wrapper {
	position: sticky;
	bottom: 0;
	z-index: 50;
	padding: 1rem 1.5rem;
	background: rgba(255, 255, 255, 0.85);
	backdrop-filter: blur(10px);
	border-top: 1px solid rgba(226, 232, 240, 1);
	box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.05);
	border-bottom-left-radius: 0.75rem; /* rounded-xl is 0.75rem */
	border-bottom-right-radius: 0.75rem;
}

.header-actions-wrapper {
	backdrop-filter: blur(8px);
	border: 1px solid rgba(226, 232, 240, 0.8);
	border-radius: 0.5rem;
	padding: 0.75rem 1rem;
	transition: all 0.3s ease;

	/* Animation for smooth appearance */
	animation: slideInFromTop 0.3s ease-out;
}

@keyframes slideInFromTop {
	from {
		opacity: 0;
		transform: translateY(-10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}
</style>
